"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import { Button } from "./button";
import type { ButtonShape, ButtonSize } from "./button";
import { CURRENT_BILLING_SCHEMA_VERSION } from "@/lib/contracts/billing";

/**
 * The one Upgrade button for the whole app. Always orange (`variant="upgrade"`)
 * and always goes STRAIGHT to Stripe checkout — it POSTs to /api/billing/checkout
 * and redirects to the hosted session, rather than routing through /pricing.
 *
 * `planId`/`cycle` are optional: the pricing & billing cards pass them (so their
 * monthly/annual toggle picks the price); a generic button (nav, Pro-upsell
 * modal) omits them and the route defaults to the paid tier + monthly. On any
 * failure it falls back to /pricing so the button never dead-ends.
 */
export interface UpgradeButtonProps {
  planId?: string;
  cycle?: "monthly" | "annual";
  /** Where Stripe returns on success / cancel (paths, origin is added). */
  successPath?: string;
  cancelPath?: string;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  children?: ReactNode;
}

const PRICING_FALLBACK = "/pricing/";

export function UpgradeButton({
  planId,
  cycle,
  successPath = "/?checkout=success",
  cancelPath = "/pricing",
  size = "md",
  shape = "rect",
  fullWidth = false,
  children = "Upgrade",
}: UpgradeButtonProps) {
  const [busy, setBusy] = useState(false);

  async function start() {
    setBusy(true);
    const checkoutAttemptId = crypto.randomUUID();
    try {
      const origin = window.location.origin;
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          schema_version: CURRENT_BILLING_SCHEMA_VERSION,
          ...(planId ? { plan_id: planId } : {}),
          ...(cycle ? { billing_cycle: cycle } : {}),
          checkout_attempt_id: checkoutAttemptId,
          success_url: `${origin}${successPath}`,
          cancel_url: `${origin}${cancelPath}`,
        }),
      });
      const data = res.ok ? ((await res.json()) as { url?: string }) : null;
      // Success → Stripe; anything else → the pricing page (never a dead end).
      window.location.href = data?.url ?? PRICING_FALLBACK;
    } catch {
      window.location.href = PRICING_FALLBACK;
    }
  }

  return (
    <Button
      variant="upgrade"
      size={size}
      shape={shape}
      fullWidth={fullWidth}
      onClick={start}
      disabled={busy}
      aria-busy={busy}
    >
      {busy ? "Starting checkout..." : children}
    </Button>
  );
}
